// Chart
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";

// Stores
import { chart } from "../../stores";

// Utils
import { utils } from "../../helpers";

// Chart register
ChartJS.register(ArcElement, Tooltip, Legend);

const classNames = require("classnames");

// WP react stuff
import { useState, useEffect } from '@wordpress/element';

/**
 * ChartPie Component
 *
 * Renders a doughnut chart with support for:
 * - Custom center label (title + number)
 * - Dynamic color mapping based on data or fallback
 * - Tooltips in percentage or euro format
 * - Global hover state via Apollo reactive var
 *
 * @param {Object} props - Component properties
 * @param {Array|Object} props.data - Data for chart (array with values or key-value object)
 * @param {string} [props.className] - Additional class names
 * @param {boolean} [props.percentage] - If true, values shown as percentages
 * @param {string} [props.number] - Number to display in chart center
 * @param {string} [props.title] - Title to display in chart center
 * @param {Object} [props.options] - Optional chart configuration overrides
 * @param {boolean} [props.options.hover] - If false, disables hover behavior
 * @param {Object} [props.options.tooltip] - Tooltip display settings
 * @param {Object} [props.options.legend] - Legend visibility settings
 * @param {string|number} [props.options.cutout] - Doughnut cutout value
 * @returns {JSX.Element} Rendered chart component
 */
const ChartPie = ({ data, className, percentage, number, title, options }) => {
  const [chartData, setChartData] = useState([]);

  const chartClass = classNames({
    "chart-pie": true,
    [className]: className,
  });

  /**
   * Effect to build dataset based on data input.
   * Supports both array-based and object-based formats.
   * Also configures backgroundColor and tooltip behavior.
   */
  useEffect(() => {
    let backgroundColors = [];

    const legends = utils.generateChartLegends(data, percentage);

    if (Array.isArray(data)) {
      backgroundColors = data.map(
        (item, index) =>
          item.color || item.backgroundColor || chart().colors[index],
      );
    } else if (typeof data === "object") {
      backgroundColors = Object.keys(data).map(
        (_, index) => chart().colors[index],
      );
    }

    const chartInfo = {
      data: legends.map((item) => item.value),
      backgroundColor: backgroundColors,
      borderWidth: 0,
    };

    if (options?.hover === false) {
      chartInfo.hoverBackgroundColor = backgroundColors;
    }

    setChartData([chartInfo]);
  }, [percentage, data, options?.hover]);

  return (
    <div
      className={chartClass}
      onMouseLeave={() => {
        chart({
          ...chart(),
          hoveredIndex: null,
        });
      }}
    >
      {number && title && (
        <div className="chart-pie__center">
          <div className="chart-pie__center__title">{title}</div>
          <div className="chart-pie__center__value">{number}</div>
        </div>
      )}

      {chartData && (
        <Doughnut
          data={{ datasets: chartData }}
          options={{
            cutout: options?.cutout ?? utils.chartConfig.cutout,
            plugins: {
              legend: {
                display: options?.legend?.display ?? false,
              },
              tooltip: {
                enabled: options?.tooltip?.enabled ?? true,
                displayColors: options?.tooltip?.displayColors ?? false,
                callbacks: {
                  label: (tooltipItem) =>
                    percentage
                      ? tooltipItem.formattedValue + "%"
                      : utils.toEuro(tooltipItem.formattedValue),
                },
              },
            },
            onHover: (e, elements) => {
              chart({
                ...chart(),
                hoveredIndex: elements.length > 0 ? elements[0].index : null,
              });
            },
          }}
        />
      )}
    </div>
  );
};

export default ChartPie;
